import { z } from "zod";
import { uuid } from "./common";

/* Public API keys and outgoing webhooks (Phase 8c). */

export const API_SCOPES = ["clinic:read", "patients:read", "appointments:read"] as const;
export type ApiScope = (typeof API_SCOPES)[number];
export const API_SCOPE_LABELS: Record<ApiScope, string> = {
  "clinic:read": "Clinic, branches, doctors and treatments",
  "patients:read": "Patients (contact details, no clinical records)",
  "appointments:read": "Appointments",
};

/** Events a webhook can receive. Only ids and minimal fields are sent, never clinical content. */
export const WEBHOOK_EVENTS = [
  "appointment.booked",
  "appointment.confirmed",
  "appointment.rescheduled",
  "appointment.cancelled",
  "appointment.checked_in",
  "appointment.completed",
  "appointment.no_show",
  "patient.created",
  "patient.updated",
  "patient.merged",
  "patient.archived",
  "invoice.issued",
  "invoice.voided",
  "payment.recorded",
  "payment.reversed",
  "refund.completed",
] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];

const cidr = z.string().trim().regex(/^[0-9a-fA-F:.]+\/\d{1,3}$/, "Use CIDR notation, e.g. 203.0.113.0/24");

export const apiClientInput = z.object({
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).nullish().transform((v) => v || null),
  allowedCidrs: z.array(cidr).max(20).nullish().transform((v) => (v && v.length ? v : null)),
  rateLimitPerMinute: z.number().int().min(1).max(10000).nullish().transform((v) => v ?? null),
});
export const apiClientStatusInput = z.object({ status: z.enum(["active", "disabled"]) });

export const apiKeyInput = z.object({
  name: z.string().trim().min(1).max(100),
  scopes: z.array(z.enum(API_SCOPES)).min(1),
  expiresAt: z.iso.datetime({ offset: true }).nullish().transform((v) => v ?? null),
});

export const webhookInput = z.object({
  url: z
    .string()
    .trim()
    .max(500)
    .regex(/^https:\/\/[^\s/?#]+/, "Use an https:// address"),
  description: z.string().trim().max(200).nullish().transform((v) => v || null),
  events: z.array(z.enum(WEBHOOK_EVENTS)).min(1),
});
export const webhookUpdateInput = z.object({
  version: z.number().int().positive(),
  status: z.enum(["active", "disabled"]).optional(),
  events: z.array(z.enum(WEBHOOK_EVENTS)).min(1).optional(),
});

export interface ApiKeyView {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: ApiScope[];
  expiresAt: string | null;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  revokedAt: string | null;
  createdAt: string;
}
export interface ApiClientView {
  id: string;
  name: string;
  description: string | null;
  status: "active" | "disabled";
  allowedCidrs: string[] | null;
  rateLimitPerMinute: number | null;
  keys: ApiKeyView[];
}
export interface WebhookView {
  id: string;
  url: string;
  description: string | null;
  events: WebhookEvent[];
  status: "active" | "disabled";
  disabledReason: string | null;
  consecutiveFailures: number;
  version: number;
  createdAt: string;
}
export interface WebhookDeliveryView {
  id: string;
  eventType: string;
  status: "pending" | "delivered" | "failed";
  attempts: number;
  responseStatus: number | null;
  lastError: string | null;
  createdAt: string;
  deliveredAt: string | null;
}
export interface IntegrationsView {
  entitled: boolean;
  clients: ApiClientView[];
  webhooks: WebhookView[];
}

/** Query for the public API list endpoints. */
export const apiListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(500).optional(),
  updatedSince: z.iso.datetime({ offset: true }).optional(),
});
export const apiAppointmentsQuery = z.object({
  from: z.iso.datetime({ offset: true }),
  to: z.iso.datetime({ offset: true }),
  branchId: uuid.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
